import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import Tick from 'aquilla/icons/actions/Tick';
import Button from 'aquilla/core/molecules/Button';
import Typography from 'aquilla/core/atoms/Typography';
import { commasIndianFormat, getParameterByName } from '@utils/textHelper';
import { segment } from '@actions/analytics';
import Image from '@base/FutureImage';
import { useEffect, useMemo } from 'react';
import { getMonthString, getYear } from '@utils/dates';
import {
  CHECKOUT_DURATION_SELECTED,
  CHECKOUT_SUBSCRIPTION_TYPE_SELECTED
} from '@cont/Checkout/constants/segment';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import { useRouter } from 'next/router';
import { CHECKOUT_STARTED } from '@constants/segment';
import usePricings from '@cont/Checkout/usePricings';
import {
  defaultSelectedPricing,
  discountedPricingIfAvailable
} from '../planSelectionHelper';

const PlanDetailCardContainer = styled.div<{ $isRecommended: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${Spacings.SPACING_1B};
  margin-left: -16px;
  margin-right: -16px;
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  background: linear-gradient(90deg, #e5fff6 0%, rgba(255, 255, 255, 0) 100%);
`;

const NormalPlanDetailCardContainer = styled.div<{ $isRecommended: boolean }>``;

const PlanDetailCardWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const PlanDetailInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_1B};
`;

const PlanDetails = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_2};
`;

const LeftInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_2B};
`;

const WarningInfoWrapper = styled.div`
  width: calc(100% - 32px);
  margin-left: ${Spacings.SPACING_8B};
  margin-top: ${Spacings.SPACING_3B};
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-i-red-4);
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_1B};
  height: ${Spacings.SPACING_7B};
  align-items: center;
  justify-content: center;
`;

const StyH7 = styled(Typography)`
  font-size: ${Fonts.H7};
  font-weight: ${Fonts.SEMIBOLD};
`;

const StyP3 = styled(Typography)<{ $hasDiscountPrice: boolean }>`
  text-decoration: ${({ $hasDiscountPrice }) =>
    $hasDiscountPrice && 'line-through'};
`;

const StyPayButton = styled(Button)`
  width: calc(100% - 32px);
  margin-left: ${Spacings.SPACING_8B};
  margin-top: ${Spacings.SPACING_3B};
`;

const ExtendSubscriptionWrapper = styled.div`
  display: flex;
  padding: ${Spacings.SPACING_6} ${Spacings.SPACING_3B};
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-i-blue-47);
  color: var(--color-i-blue);
  font-size: ${Fonts.P2};
  font-weight: ${Fonts.SEMIBOLD};
  width: calc(100% - 32px);
  margin-left: ${Spacings.SPACING_8B};
  margin-top: ${Spacings.SPACING_3B};
`;

const PlanPricingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_1B};
  align-items: end;
`;

const RecommendedTag = styled.div`
  display: flex;
  flex-direction: row;
  width: 110px;
  margin-left: ${Spacings.SPACING_8B};
  border-radius: ${Spacings.SPACING_1B};
  gap: ${Spacings.SPACING_1B};
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_6};
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.HEAVY};
  background-color: var(--color-i-green);
  color: var(--color-i-white);
`;

export const getDescriptionArray = (value) => {
  if (!value) return [];
  const descArray = Object.values(value);
  const finalArray = descArray.slice(1).filter((value) => value !== null);
  return finalArray as Array<string>;
};

const PlanDetailCard = ({
  subscription,
  index,
  onClick,
  planSelection,
  isReferralCodeApplicable = true,
  isReferralAdded = false,
  isRecommended = false,
  onSubmit = (props) => {},
  showDuration = false,
  goalDurationInfo = { durationInfo: {} },
  discountedPricings = []
}) => {
  const router = useRouter();

  const { query: { goalUID = '', referral_code = '' } = {} } = router ?? {};

  const { pricings } = usePricings({ goalUID });

  const descriptionArray = useMemo(() => {
    return getDescriptionArray(goalDurationInfo?.durationInfo);
  }, [goalDurationInfo?.durationInfo]);

  const {
    uid: subscriptionId,
    display_duration,
    price,
    checkout_dates,
    checkout_dates_with_benefits: {
      ends_at = '',
      starts_at = ''
    } = checkout_dates ?? {},
    display_data_v2,
    tax_breakdown = [],
    display_data: { unit_price = {} } = display_data_v2
  } = subscription ?? {};

  let discountedPrice = null;

  const modifiedDisplayDuration = display_duration.replace(/\s+/g, '-');

  const displayDurationTitle = `${modifiedDisplayDuration} plan (${getMonthString(
    ends_at
  )} ${getYear(ends_at)})`;

  if (tax_breakdown.length > 0 && isReferralCodeApplicable)
    discountedPrice = tax_breakdown.find((tax) => {
      return tax?.label === 'Total';
    });

  const isSelected = index === planSelection;

  const RenderContainer = isRecommended
    ? PlanDetailCardContainer
    : NormalPlanDetailCardContainer;

  const radioButtonUrl = `newPlanSelection/${
    isSelected ? 'StyRadioChecked' : 'StyRadioUnchecked'
  }.svg`;

  const eventProps = useCheckoutEventProps(
    goalUID,
    discountedPricingIfAvailable(
      subscriptionId,
      isReferralAdded,
      discountedPricings
    ) || defaultSelectedPricing(subscriptionId, pricings),
    false
  );

  const goalEventProps = useCheckoutEventProps(goalUID);

  useEffect(() => {
    if (
      (referral_code && isSelected && isReferralAdded) ||
      (!referral_code && isSelected)
    ) {
      const isDeeplink =
        getParameterByName('deeplink', router.asPath) === 'true';
      const isBanner = getParameterByName('banner', router.asPath) === 'true';
      let lpss = 'NA';
      if (isDeeplink) {
        lpss = 'Deeplink';
      } else if (isBanner) {
        lpss = 'Banner';
      }
      segment.track(CHECKOUT_DURATION_SELECTED, eventProps);
      segment.track(CHECKOUT_STARTED, {
        ...eventProps,
        last_primary_source_section: lpss
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, router.asPath, isReferralAdded]);

  return (
    <RenderContainer $isRecommended={isRecommended}>
      {isRecommended && (
        <RecommendedTag>
          <Image
            src="newPlanSelection/planCardStars.svg"
            width={12}
            height={12}
            alt="Stars"
          />
          RECOMMENDED
        </RecommendedTag>
      )}
      <PlanDetailCardWrapper onClick={onClick}>
        <LeftInfoWrapper>
          <Image
            src={radioButtonUrl}
            width={24}
            height={24}
            alt="Radio Button"
          />
          <PlanDetailInfoWrapper>
            <Typography variant="h5">{displayDurationTitle}</Typography>
            {descriptionArray.map((detail) => {
              return (
                <PlanDetails key={detail}>
                  <Tick width={16} height={16} color="var(--color-i-gray-13)" />
                  <Typography variant="p3" color="var(--color-i-gray-13)">
                    {detail}
                  </Typography>
                </PlanDetails>
              );
            })}
          </PlanDetailInfoWrapper>
        </LeftInfoWrapper>
        <PlanPricingWrapper>
          <Typography variant="h5">
            ₹
            {!discountedPrice
              ? commasIndianFormat(price, false)
              : commasIndianFormat(discountedPrice?.amount, false)}
          </Typography>
          {discountedPrice && isReferralAdded && (
            <StyP3
              variant="p3"
              color="var(--color-i-gray-13)"
              $hasDiscountPrice={!!discountedPrice}
            >
              ₹{commasIndianFormat(price, false)}
            </StyP3>
          )}
          <Typography variant="p3" color="var(--color-i-gray-13)">
            ₹{commasIndianFormat(unit_price?.value, false)} /mo
          </Typography>
        </PlanPricingWrapper>
      </PlanDetailCardWrapper>
      {!isReferralCodeApplicable && (
        <WarningInfoWrapper>
          <Image
            src="newPlanSelection/warningIcon.svg"
            width={16}
            height={16}
            alt="Warning"
          />
          <StyH7 variant="h6" color="var(--color-i-red)">
            Coupon not applicable on this duration
          </StyH7>
        </WarningInfoWrapper>
      )}
      {isSelected && showDuration && (
        <ExtendSubscriptionWrapper>
          Extend subscription from {starts_at} to {ends_at}.
        </ExtendSubscriptionWrapper>
      )}
      {isSelected && (
        <StyPayButton
          variant="primary"
          size="large"
          onClick={() => {
            segment.track(CHECKOUT_SUBSCRIPTION_TYPE_SELECTED, {
              ...goalEventProps,
              subscription_type: 'PLUS'
            });
            onSubmit(subscription);
          }}
        >
          Continue to pay ₹
          {!discountedPrice
            ? commasIndianFormat(price, false)
            : commasIndianFormat(discountedPrice?.amount, false)}
        </StyPayButton>
      )}
    </RenderContainer>
  );
};

export default PlanDetailCard;
